import { NativeSelect, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useState } from "react";
import { Async, Badge, Empty, PageHead } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Citations } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync } from "@/lib/hooks";

export function CitationsPage() {
	const t = useT();
	const { locale } = useI18n();
	const [week, setWeek] = useState<string | undefined>(undefined);
	const state = useAsync<Citations>(() => api.citations(week), [week]);

	return (
		<>
			<PageHead title={t("citations.title")} lede={t("citations.lede")}>
				{state.data ? (
					<NativeSelect
						aria-label={t("citations.week")}
						value={state.data.week}
						onChange={(e) => setWeek(e.target.value)}
					>
						{[state.data.week, ...state.data.weeks.filter((w) => w !== state.data?.week)].map((w) => (
							<option key={w} value={w}>
								{w}
							</option>
						))}
					</NativeSelect>
				) : null}
			</PageHead>
			<Async state={state}>
				{(c) => (
					<div className="stack">
						<div className="row">
							<Badge tone={c.engines_passing >= c.engines_needed ? "success" : "warning"}>
								{t("citations.passing", { n: c.engines_passing, total: c.gate.length })}
							</Badge>
							<span className="muted">{t("citations.needed", { n: c.engines_needed })}</span>
						</div>

						{c.gate.length === 0 ? (
							<Empty body={t("citations.noData")} />
						) : (
							<div className="table-scroll">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("citations.engine")}</TableHead>
											<TableHead className="num">{t("citations.questionsPassing")}</TableHead>
											<TableHead className="num">{t("citations.questions")}</TableHead>
											<TableHead className="num">{t("citations.rate")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{c.gate.map((g) => (
											<TableRow key={g.engine}>
												<TableCell>{g.label}</TableCell>
												<TableCell className="num">{g.passing}</TableCell>
												<TableCell className="num">{g.questions}</TableCell>
												<TableCell className="num">
													<Badge tone={g.rate >= 0.2 ? "success" : "danger"}>{fmt.percent(locale, g.rate)}</Badge>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}

						<div className="grid-2">
							<div className="card">
								<h2>{t("citations.raw")}</h2>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("citations.engine")}</TableHead>
											<TableHead className="num">{t("citations.cited")}</TableHead>
											<TableHead className="num">{t("citations.questions")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{c.raw.map((r) => (
											<TableRow key={r.engine}>
												<TableCell>{r.label}</TableCell>
												<TableCell className="num">{r.cited}</TableCell>
												<TableCell className="num">{r.questions}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<div className="card">
								<h2>{t("citations.byKind")}</h2>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("citations.kind")}</TableHead>
											<TableHead className="num">{t("citations.cited")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{c.by_kind.map((k) => (
											<TableRow key={k.kind}>
												<TableCell>{k.kind}</TableCell>
												<TableCell className="num">{k.count}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</div>
					</div>
				)}
			</Async>
		</>
	);
}
